import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
} from "@mui/material";
import type { DelegateItem, DelegateListItem } from "src/api/delegate/delegateApi";

export type DelegateUpsertPayload = {
  delegateName: string;
  delegateDesc: string;
  file?: File | null;
};

type Props = {
  open: boolean;
  item: DelegateItem | null; // detail(수정일 때)
  listItem: DelegateListItem | null; // 리스트에서 클릭한 원본(옵션)
  loading?: boolean;
  onClose: () => void;
  onSubmit: (payload: DelegateUpsertPayload) => Promise<void>;
};

const DelegateUpsertDialog = ({
  open,
  item,
  listItem,
  loading = false,
  onClose,
  onSubmit,
}: Props) => {
  const [delegateName, setDelegateName] = React.useState("");
  const [delegateDesc, setDelegateDesc] = React.useState("");
  const [file, setFile] = React.useState<File | null>(null);

  React.useEffect(() => {
    if (!open) return;

    // 수정이면 detail 기준으로 채움
    if (item) {
      setDelegateName(item.delegateName ?? "");
      setDelegateDesc(item.delegateDesc ?? "");
    } else if (listItem) {
      // 혹시 detail 늦을 때라도 기본 값
      setDelegateName((listItem as any).delegateName ?? "");
      setDelegateDesc((listItem as any).delegateDesc ?? "");
    } else {
      setDelegateName("");
      setDelegateDesc("");
    }
    setFile(null);
  }, [open, item, listItem]);

  const disabled = loading || !delegateName.trim() || !delegateDesc.trim();

  const currentFileUrl = item?.files?.[0]?.fileUrl;

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 900 }}>{item ? "입점사 수정" : "입점사 등록"}</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="입점사명 (delegateName)"
            value={delegateName}
            onChange={(e) => setDelegateName(e.target.value)}
            disabled={loading}
            fullWidth
            size="small"
          />
          <TextField
            label="설명 (delegateDesc)"
            value={delegateDesc}
            onChange={(e) => setDelegateDesc(e.target.value)}
            disabled={loading}
            fullWidth
            size="small"
            multiline
            minRows={3}
          />

          {currentFileUrl ? (
            <Stack spacing={1}>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                현재 이미지
              </Typography>
              <img
                src={currentFileUrl}
                alt="delegate"
                style={{ width: 180, height: 180, objectFit: "cover", borderRadius: 8 }}
              />
            </Stack>
          ) : null}

          <Stack spacing={1}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              이미지 파일 (file)
            </Typography>
            <input
              type="file"
              accept="image/*"
              disabled={loading}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            {file ? (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                선택됨: {file.name}
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading} variant="outlined">
          취소
        </Button>
        <Button
          onClick={async () => {
            await onSubmit({
              delegateName: delegateName.trim(),
              delegateDesc: delegateDesc.trim(),
              file,
            });
          }}
          disabled={disabled}
          variant="contained"
        >
          {loading ? "저장 중..." : "저장"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DelegateUpsertDialog;
